<script setup>
import Table from '@/@core/components/Table.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import AuditService from '@/services/incident/audit.service'
import AuditDrawer from '@/views/incident/master/audit/AuditDrawer.vue'
import { useRouter } from 'vue-router'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import AuditScheduleDrawer from '@/views/incident/master/audit/AuditScheduleDrawer.vue'
import AuditScheduleService from '@/services/incident/audit-schedule.service'

definePage({
  meta: {
    name: 'incident-master-audit',
  },  
})

const router = useRouter()

// Data table options
const searchQuery = ref()
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  { title: '', key: 'data-table-expand' },
  {
    title: 'Title',
    key: 'title',
  },

  // {
  //   title: 'department',
  //   key: 'department',
  // },
  {
    title: 'Repeatable Duration',
    key: 'repeat_duration',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: auditListing,
  execute: fetchAudits,
} = await useApi(createUrl('/masters/incident-audits', {
  query: {
    paged: 1,
    q: searchQuery,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const audits = computed(() => auditListing.value?.data)
const totalAudits = computed(() => audits.value?.meta?.total)

const isDrawerVisible = ref(false)
const isViewOnly = ref(false)
const auditData = ref()

const showAudit = (data = null, viewOnly = false) => {
  auditData.value = data
  isDrawerVisible.value = true
  isViewOnly.value = viewOnly
}

watch(isDrawerVisible, value => {
  if (value === false) {
    auditData.value = null
    isViewOnly.value = false
  }
})

const refDeleteDialog = ref()
const isDeleteDialogVisible = ref(false)

const showDeleteDialog = data => {
  auditData.value = data
  isDeleteDialogVisible.value = true
}

const deleteAudit = async (confirm, data) => {
  if(data?.id && confirm){
    await AuditService.destroy(
      data.id, 
      () => {
        refDeleteDialog.value.onSuccess()
        isDeleteDialogVisible.value = false
        fetchAudits()
      },
      () => {
        refDeleteDialog.value.onError()
      },
    )
  }
}

watch(isDeleteDialogVisible, value => {
  if (value === false) auditData.value = null
})

// const decodeTime = (value, type = 'minute') => {
//   let days
//   let hours
//   let minutes

//   if (type == 'minute') {
//     days = Math.floor(value / (24 * 60))

//     const remainingMinutesAfterDays = value % (24 * 60)

//     hours = Math.floor(remainingMinutesAfterDays / 60)
//     minutes = remainingMinutesAfterDays % 60
//   }

//   return `${days != 0 ? (days + 'D') : ''} ${hours != 0 ? (hours + 'H') : ''} ${minutes != 0 ? (minutes + 'M') : ''}`
// }

const isAuditScheduleDrawerVisible = ref(false)
const auditScheduleData = ref()
const isAuditScheduleViewOnly = ref(false)

const showAuditSchedule = (data = null, viewOnly = false) => {
  auditScheduleData.value = data
  isAuditScheduleDrawerVisible.value = true
  isAuditScheduleViewOnly.value = viewOnly
}

const refAuditScheduleDeleteDialog = ref()
const isAuditScheduleDeleteDialogVisible = ref(false)

const showAuditScheduleDeleteDialog = data => {
  auditScheduleData.value = data
  isAuditScheduleDeleteDialogVisible.value = true
}

const deleteAuditSchedule = async (confirm, data) => {
  if(data?.id && confirm){
    await AuditScheduleService.destroy(
      data.id, 
      () => {
        refAuditScheduleDeleteDialog.value.onSuccess()
        isAuditScheduleDeleteDialogVisible.value = false
        fetchAudits()
      },
      () => {
        refAuditScheduleDeleteDialog.value.onError()
      },
    )
  }
}

watch(isAuditScheduleDeleteDialogVisible, value => {
  if (value === false) auditScheduleData.value = null
})
</script>

<template>
  <section>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <!-- 👉 Export button -->
          <!--
            <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-screen-share"
            >
            Export
            </VBtn> 
          -->
       
          <VBtn
            v-if="$can('create', 'master.audit')"
            prepend-icon="tabler-plus"
            @click="showAudit(null)"
          >
            Add Audit
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="audits"
        :items-length="totalAudits"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.department="{ item }">
          {{ item?.department?.name }}
        </template>

        <template #item.repeat_duration="{ item }">
          <!-- {{ item?.repeat_duration ? decodeTime(item?.repeat_duration) : 'N/A' }} -->
          {{ item.repeat_duration ? `${item.repeat_duration} Days` : 'N/A' }}
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <!--
            <RouterLink :to="{ name: 'app-incident-master-audit-questionnaire', params: { id: item.id } }">
            <VBtn
            variant="tonal"
            density="compact"
            >
            Question Set
            </VBtn>
            </RouterLink> 
          -->

          <IconBtn @click="router.push({ name: 'app-incident-master-audit-questionnaire', params: { id: item.id } })">
            <VIcon icon="tabler-notes" />
          </IconBtn>

          <IconBtn 
            v-if="$can('update', 'master.audit')"
            @click="showAudit(item)"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>

          <IconBtn 
            v-if="$can('delete', 'master.audit')"
            @click="showDeleteDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>
        </template>

        <!-- Audit Schedules -->
        <template #expanded-row="{ item }">
          <tr class="v-data-table__tr evel-table-custom-bg-color">
            <td
              colspan="12"
              class="pb-4"
              style="background-color: #f7f7f7;"
            >
              <VCard variant="text">
                <VCardText>
                  <div class="d-flex align-center justify-space-between">
                    <div class="text-h5">
                      Schedule Audits
                    </div>
                    <VBtn
                      density="compact"
                      @click="showAuditSchedule({ incident_audit_id: item.id })"
                    >
                      Create Schedule
                    </VBtn>
                  </div>
                  <Table
                    class="mt-4"
                    :columns="[
                      { key: 'start_date', title: 'Start Date' },
                      { key: 'end_date', title: 'End Date' },
                      { key: 'auditors', title: 'Auditors' },
                      { key: 'verifiers', title: 'Verifiers' },
                      { key: 'actions', title: 'Actions' },
                    ]"
                    :items="item.schedules"
                  >
                    <template #start_date="{ item }">
                      {{ formatDate(item.start_date) }}
                    </template>
                    <template #end_date="{ item }">
                      {{ item.end_date ? formatDate(item.end_date) : 'N/A' }}
                    </template>
                    <template #auditors="{ item }">
                      <div class="v-avatar-group">
                        <template
                          v-for="(user, index) in item.auditors"
                          :key="index"
                        >
                          <VAvatar
                            size="36"
                            :color="$vuetify.theme.current.dark ? '#f6f6f7' : '#4A5072'"
                          >
                            <VTooltip
                              activator="parent"
                              location="end"
                            >
                              {{ user.name }}
                            </VTooltip>
                            <VImg 
                              v-if="user.avatar"
                              :src="user.avatar"
                            />
                            <span v-else>
                              {{ avatarText(user.name) }}
                            </span>
                          </VAvatar>
                        </template>
                      </div>
                    </template>
                    <template #verifiers="{ item }">
                      <div class="v-avatar-group">
                        <template
                          v-for="(user, index) in item.verifiers"
                          :key="index"
                        >
                          <VAvatar
                            size="36"
                            :color="$vuetify.theme.current.dark ? '#f6f6f7' : '#4A5072'"
                          >
                            <VTooltip
                              activator="parent"
                              location="end"
                            >
                              {{ user.name }}
                            </VTooltip>
                            <VImg 
                              v-if="user.avatar"
                              :src="user.avatar"
                            />
                            <span v-else>
                              {{ avatarText(user.name) }}
                            </span>
                          </VAvatar>
                        </template>
                      </div>
                    </template>
                    <template #actions="{ item }">
                      <IconBtn 
                        v-if="$can('update', 'master.audit.schedule')"
                        @click="showAuditSchedule(item)"
                      >
                        <VIcon icon="tabler-edit" />
                      </IconBtn>

                      <IconBtn 
                        v-if="$can('delete', 'master.audit.schedule')"
                        @click="showAuditScheduleDeleteDialog(item)"
                      >
                        <VIcon icon="tabler-trash" />
                      </IconBtn>
                    </template>
                  </Table>
                </VCardText>
              </VCard>
            </td>
          </tr>
        </template>

        <!-- pagination -->
        <!--
          <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
          <p class="text-sm text-disabled mb-0">
          {{ paginationMeta({ page, itemsPerPage }, totalAudits) }}
          </p>

          <VPagination
          v-model="page"
          :length="Math.ceil(totalAudits / itemsPerPage)"
          :total-visible="$vuetify.display.xs ? 1 : Math.ceil(totalAudits / itemsPerPage)"
          >
          <template #prev="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Previous
          </VBtn>
          </template>

          <template #next="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Next
          </VBtn>
          </template>
          </VPagination>
          </div>
          </template> 
        -->
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>

    <AuditDrawer
      v-model:isDrawerOpen="isDrawerVisible"
      :form-data="auditData"
      :view-only="isViewOnly"
      @refetch-listing="fetchAudits"
    />

    <ConfirmDialog
      ref="refDeleteDialog"
      v-model:isDialogVisible="isDeleteDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Audit Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="auditData"
      @confirm="deleteAudit"
    />

    <AuditScheduleDrawer 
      v-model:isDrawerOpen="isAuditScheduleDrawerVisible"
      :form-data="auditScheduleData"
      :view-only="isAuditScheduleViewOnly"
      @refetch-listing="fetchAudits"
    />

    <ConfirmDialog
      ref="refAuditScheduleDeleteDialog"
      v-model:isDialogVisible="isAuditScheduleDeleteDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Audit Schedule Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="auditScheduleData"
      @confirm="deleteAuditSchedule"
    />
  </section>
</template>
